import { useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'gatsby-plugin-react-intl';
import { BLOCKS } from '@contentful/rich-text-types';
import { slide as Menu } from 'react-burger-menu';

import Link from '../../Core/Link';
import RichText, { fragment } from '../../Core/RichText';
import { Context } from '../../Providers/Blade';
import GlobalSearch from '../GlobalSearch';
import { Header, Logo, CloseBtn } from '../Blade/styles';

import {
  Wrapper,
  BladeButton,
  CTAWrapper,
  NavButton,
  ProductWrapper,
  Links,
  CtaWrapper,
  Heading,
  Body,
} from './styles';

const heading = {
  renderNode: {
    [BLOCKS.PARAGRAPH]: (_, children) => <Heading>{children}</Heading>,
    [BLOCKS.HEADING_2]: (_, children) => (
      <Heading className="no-border">{children}</Heading>
    ),
  },
};

const body = {
  renderNode: {
    [BLOCKS.PARAGRAPH]: (_, children) => <Body>{children}</Body>,
  },
};

const ApplicationMenu = ({ data }) => {
  if (!data) return null;
  const { state, updateBladeState, resetState, closeAllMenusOnEsc } =
    useContext(Context);

  const intl = useIntl();

  const {
    id,
    component,
    linkText,
    type,
    ctaButtonSize,
    productCards,
    ctas,
    logoImageDam,
    searchFieldText,
  } = data;

  const appIdHandle = `app-menu-${linkText}-${id}`
    .replace(/\s+/g, '-')
    .toLowerCase();

  const handleClick = menuState => {
    const menuBladeState = {};
    menuBladeState[appIdHandle] = menuState;
    updateBladeState(menuBladeState);
  };

  useEffect(() => {
    handleClick(false);
  }, []);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      document.body.parentElement.style.overflow = Object.values(
        state,
      ).includes(true)
        ? 'hidden'
        : 'auto';
    }
  }, [state]);

  return (
    <Wrapper className={component}>
      <BladeButton
        isOpen={state[appIdHandle]}
        className={`${component} ${type} ${ctaButtonSize}`}
        onClick={() => {
          handleClick(true);
        }}
        aria-label={
          intl.locale === 'en'
            ? `Open ${linkText} modal`
            : `Ouvrez ${linkText} modal`
        }
      >
        {linkText}
      </BladeButton>
      <Menu
        right
        noOverlay
        htmlClassName="app-blade"
        customBurgerIcon={false}
        customCrossIcon={false}
        isOpen={state[appIdHandle]}
        customOnKeyDown={closeAllMenusOnEsc}
      >
        <Header>
          {logoImageDam ? (
            <Logo
              src={logoImageDam.asset.localImage.publicURL}
              alt={logoImageDam.altText || ''}
            />
          ) : (
            <p>{linkText}</p>
          )}
          <CloseBtn
            aria-label={`${intl.formatMessage({ id: 'close' })}`}
            onClick={() => resetState()}
          >
            <span>{intl.formatMessage({ id: 'close' })}</span>
          </CloseBtn>
        </Header>
        {searchFieldText && (
          <GlobalSearch
            id="main-menu-search"
            type="main-menu is-open"
            placeholder={searchFieldText}
            open={state[appIdHandle]}
          />
        )}
        <ProductWrapper>
          <Links>
            <li>
              <NavButton type="button" onClick={() => handleClick(false)}>
                {intl.formatMessage({ id: 'back' })}
              </NavButton>
            </li>
            {productCards &&
              productCards.map(card => {
                return (
                  <li key={card.id} className="product-block">
                    <RichText document={card.header} options={heading} />
                    <RichText document={card.bodyCopy} options={body} />
                    <CtaWrapper>
                      {card.ctas &&
                        card.ctas.map(cta => {
                          if (cta.hideFrench === true) return null;
                          return (
                            <Link
                              key={cta.id}
                              data={cta}
                              className="unstyled-link nav-tab"
                              blade
                              resetState={resetState}
                            >
                              <RichText
                                document={cta.label}
                                options={fragment}
                              />
                            </Link>
                          );
                        })}
                    </CtaWrapper>
                  </li>
                );
              })}
          </Links>
          {ctas && (
            <CTAWrapper>
              {ctas &&
                ctas.map(cta => {
                  if (cta.hideFrench === true) return null;
                  return (
                    <Link key={cta.id} data={cta}>
                      <RichText document={cta.label} options={fragment} />
                    </Link>
                  );
                })}
            </CTAWrapper>
          )}
        </ProductWrapper>
      </Menu>
    </Wrapper>
  );
};

ApplicationMenu.propTypes = {
  data: PropTypes.shape().isRequired,
};

export default ApplicationMenu;
